import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/auth.service';
import { authEvents } from '@/lib/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'MANAGER' | 'STAFF';
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  hasHydrated: boolean;
  
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
  setHasHydrated: (value: boolean) => void;
}

const initialState = {
  user: null as User | null,
  isAuthenticated: false,
  isLoading: false,
  error: null as string | null,
  hasHydrated: false,
};

// Create store first, then set up event listeners
const createAuthStore = () => {
  const store = create<AuthState>()(
    persist(
      (set, get) => ({
        ...initialState,

      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await authService.login(email, password);
          // Tokens are now in HttpOnly cookies, only user data in response
          const { user } = response.data;

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Login failed';
          set({ isLoading: false, error: errorMessage });
          throw new Error(errorMessage);
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ isLoading: true, error: null });
          
          // console.log('Attempting registration with data:', { ...data, password: '[REDACTED]' }); // DISABLED TO STOP SPAM
          const response = await authService.register(data);
          // console.log('Registration response:', response); // DISABLED TO STOP SPAM
          
          // Tokens are now in HttpOnly cookies, only user data in response
          const { user } = response.data;

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          // console.error('Registration error:', error); // DISABLED TO STOP SPAM
          // console.error('Error response:', error.response); // DISABLED TO STOP SPAM
          const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
          set({ isLoading: false, error: errorMessage });
          throw new Error(errorMessage);
        }
      },

      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          // Even if logout request fails, clear local state
          // console.error('Logout error:', error); // DISABLED TO STOP SPAM
        } finally {
          // Clear auth service state
          authService.clearAuthState();
          
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      refreshAccessToken: async () => {
        try {
          const response = await authService.refresh();
          // Tokens are refreshed in HttpOnly cookies automatically
          if (response.refreshed) {
            // Token refresh was successful, no state update needed
            // Cookies are handled by the browser automatically
            return true;
          }
          return false;
        } catch (error: any) {
          // Don't trigger logout here - let the API interceptor handle it via events
          // This prevents circular calls between refreshAccessToken and API interceptor
          throw error;
        }
      },

      updateProfile: async (data: Partial<User>) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await authService.updateProfile(data);
          const updatedUser = response.data;

          set({
            user: updatedUser,
            isLoading: false,
          });
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Profile update failed';
          set({ isLoading: false, error: errorMessage });
          throw new Error(errorMessage);
        }
      },

      changePassword: async (oldPassword: string, newPassword: string) => {
        try {
          set({ isLoading: true, error: null });
          
          await authService.changePassword(oldPassword, newPassword);

          set({
            isLoading: false,
          });
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Password change failed';
          set({ isLoading: false, error: errorMessage });
          throw new Error(errorMessage);
        }
      },

      checkAuth: async () => {
        try {
          set({ isLoading: true });

          const response = await authService.getProfile();
          // Extract user from nested response structure: {success, data: {...user...}, message}
          const user = response.data.data || response.data;

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // Token is invalid or missing, clear auth state
          authService.clearAuthState();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

        clearError: () => {
          set({ error: null });
        },
        setHasHydrated: (value: boolean) => {
          set({ hasHydrated: value });
        },
      }),
      {
        name: 'auth-storage',
        // Only persist user info and auth status, not tokens (they're in HttpOnly cookies)
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    )
  );

  const markHydrated = () => {
    const { setHasHydrated } = store.getState();
    setHasHydrated(true);
  };
  if (store.persist?.hasHydrated?.()) {
    markHydrated();
  } else {
    store.persist?.onFinishHydration?.(() => {
      markHydrated();
    });
  }

  // Set up event listeners for session expiration
  authEvents.subscribe('session-expired', () => {
    const state = store.getState();
    // Only handle if currently authenticated to prevent loops
    if (state.isAuthenticated) {
      // Clear auth service state first
      authService.clearAuthState();
      
      // Clear local storage
      localStorage.removeItem('auth-storage');
      
      // Update store state without triggering API calls
      store.setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Your session has expired. Please login again.',
      });
      
      // Redirect to login if not already there — use replace() to prevent back-button returning to authenticated page
      if (!window.location.pathname.includes('/login')) {
        window.location.replace('/login');
      }
    }
  });

  return store;
};

export const useAuthStore = createAuthStore();
